'use client'

import Image from 'next/image'
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'

import { AnimatePresence, motion } from 'framer-motion'
import { Trash2, X } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'

interface ImageObject {
  id: number | string
  image: string
}

interface ImagePreviewProps {
  children: (
    images: ProcessedImage[],
    onThumbnailClick: (index: number) => void
  ) => ReactNode
  onDelete?: (index: number) => void
  source: ImageSource | ImageSource[]
}

type ImageSource = File | ImageObject | string

interface ProcessedImage {
  id: string
  url: string
}

export const ImagePreview = ({
  children,
  onDelete,
  source
}: ImagePreviewProps) => {
  const t = useTranslations('components.imagePreview')
  const [isOpen, setIsOpen] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)

  const images = useMemo((): ProcessedImage[] => {
    const sourceArray = Array.isArray(source) ? source : [source]

    return sourceArray.map((item, index) => {
      if (typeof item === 'string') {
        return { id: item, url: item }
      }
      if (item instanceof File) {
        return {
          id: `${item.name}-${item.lastModified}-${index}`,
          url: URL.createObjectURL(item)
        }
      }
      return { id: String(item.id), url: item.image }
    })
  }, [source])

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  if (isOpen) {
    if (images.length === 0) {
      setIsOpen(false)
    } else if (currentIndex >= images.length) {
      setCurrentIndex(images.length - 1)
    }
  }

  useEffect(
    () => () => {
      images.forEach((image) => {
        if (image.url.startsWith('blob:')) {
          URL.revokeObjectURL(image.url)
        }
      })
    },
    [images]
  )

  const handleOpen = (index: number) => {
    setCurrentIndex(index)
    setIsOpen(true)
  }

  const handleDelete = () => {
    if (onDelete) {
      onDelete(currentIndex)
    }
  }

  const selectedImage = images[currentIndex]

  return (
    <>
      {children(images, handleOpen)}
      <Dialog onOpenChange={setIsOpen} open={isOpen}>
        <DialogContent
          className="max-w-3xl border-none bg-black/90 p-0 backdrop-blur-sm"
          showCloseButton={false}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>{t('title')}</DialogTitle>
          </DialogHeader>

          {/* Close Button */}
          <Button
            aria-label={t('closeAriaLabel')}
            className="absolute end-4 top-4 z-20 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 focus:ring-2 focus:ring-white/50"
            onClick={handleClose}
            size="icon"
            variant="ghost"
          >
            <X size={20} />
          </Button>

          <div className="relative flex h-[70vh] w-full items-center justify-center overflow-hidden">
            <AnimatePresence initial={false} mode="wait">
              <motion.div
                animate={{ opacity: 1 }}
                className="relative flex h-full w-full items-center justify-center overflow-hidden"
                exit={{ opacity: 0 }}
                initial={{ opacity: 0 }}
                key={currentIndex}
                transition={{ duration: 0.2 }}
              >
                {selectedImage && (
                  <Image
                    alt={t('imageAlt', {
                      current: String(currentIndex + 1),
                      total: String(images.length)
                    })}
                    className="max-h-full max-w-full object-contain"
                    height={600}
                    priority
                    src={selectedImage.url}
                    unoptimized
                    width={800}
                  />
                )}
              </motion.div>
            </AnimatePresence>

            {/* Delete Button */}
            {onDelete && (
              <Button
                aria-label={t('deleteAriaLabel')}
                className="absolute start-1/2 bottom-4 z-10 -translate-x-1/2 bg-red-600 text-white hover:bg-red-700 focus:ring-2 focus:ring-red-500 rtl:translate-x-1/2"
                onClick={handleDelete}
                size="sm"
                variant="destructive"
              >
                <Trash2 className="me-2" size={16} />
                {t('deleteButton')}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
